import {
  computed,
  ssrRef,
  useFetch,
  useRoute,
  watch,
} from '@nuxtjs/composition-api';
import {
  PreparedBlock,
  SectionsLoadersMap,
  SectionLoaderResult,
  PreloadedDataStructure,
  SectionDataHook,
  UseSectionsPreloadDataProps,
  UseSectionsPreloadDataReturn,
  PrepareDynamicBlock,
} from './types';
import sectionDataHooks from './data-loaders';

/**
 * Creates an array of promises for preloading section data
 * @param sectionsToPreloadData - Map of sections and loaders to preload
 * @returns Array of promises with section loading results
 */
const prepareSectionLoadPromises = (
  sectionsToPreloadData: SectionsLoadersMap,
): Promise<SectionLoaderResult>[] => {
  const promises: Promise<SectionLoaderResult>[] = [];

  Object.entries(sectionsToPreloadData).forEach(
    ([sectionType, indexedLoaders]) => {
      Object.entries(indexedLoaders).forEach(([index, fetchFn]) => {
        promises.push(
          (async () => {
            try {
              const data = await fetchFn();
              return { sectionType, index, data, error: null };
            } catch (error: any) {
              console.error(
                `Error while prefetch data for section ${sectionType} at index ${index}:`,
                error,
              );
              return { sectionType, index, data: null, error };
            }
          })(),
        );
      });
    },
  );

  return promises;
};

/**
 * Processes section loading results into structured data
 * @param results - Results from section loading promises
 * @returns Structured preloaded data by section type and index
 */
const processLoadedResults = (
  results: SectionLoaderResult[],
): PreloadedDataStructure => {
  const loadedData: PreloadedDataStructure = {};

  results.forEach(({ sectionType, index, data }) => {
    if (!data) return;

    if (!loadedData[sectionType]) {
      loadedData[sectionType] = { [index]: data };
    } else {
      loadedData[sectionType] = {
        ...loadedData[sectionType],
        [index]: data,
      };
    }
  });

  return loadedData;
};

/**
 * Hook for preloading section data
 * @param pageBlocksProps - Properties with blocks to preload
 * @param prepareDynamicBlock - Function to prepare dynamic blocks
 * @returns Object with enhanced components
 */
export default function useSectionsPreloadData(
  pageBlocksProps: UseSectionsPreloadDataProps,
  prepareDynamicBlock: PrepareDynamicBlock,
): UseSectionsPreloadDataReturn {
  const initSectionDataHooks: Record<string, SectionDataHook> =
    Object.fromEntries(
      Object.entries(sectionDataHooks).map(([sectionKey, loaderHook]) => [
        sectionKey,
        loaderHook(),
      ]),
    );
  const ssrKey = `useSectionsPreloadData-${useRoute().value.path}`;
  const preloadedData = ssrRef<PreloadedDataStructure | null>(null, ssrKey);

  const enhancedComponents = computed<PreparedBlock[]>(() =>
    (pageBlocksProps?.blocks || []).map((block, index) => {
      if (!prepareDynamicBlock)
        throw new Error(
          'Pass prepareDynamicBlock wrapped with project options to useSectionsPreloadData hook',
        );
      const { type, data } = prepareDynamicBlock(block);
      return {
        type,
        data: preloadedData.value?.[type]?.[index] || data,
      };
    }),
  );

  const sectionsToPreloadData = computed<SectionsLoadersMap>(() => {
    const map: SectionsLoadersMap = {};
    // Create map with loaders per section by instance index from page blocks
    // { 'section-a': { '0': fn, 2: fn }, 'section-b': { 1: fn } }
    enhancedComponents.value.forEach((component, index) => {
      const loader = initSectionDataHooks[component.type]?.load;
      if (typeof loader !== 'function') return;

      if (!map[component.type]) {
        map[component.type] = { [index]: () => loader(component.data) };
      } else {
        map[component.type] = {
          ...map[component.type],
          [index]: () => loader(component.data),
        };
      }
    });
    return map;
  });

  const hasSectionsToPreload = computed<boolean>(
    () => Object.keys(sectionsToPreloadData.value).length > 0,
  );

  const { fetch } = useFetch(async () => {
    if (!hasSectionsToPreload.value) return;

    const promises = prepareSectionLoadPromises(sectionsToPreloadData.value);
    const results = await Promise.all(promises);

    preloadedData.value = processLoadedResults(results);
  });

  // handle back forward cache, fetch data if cache is empty for 'back direction' page
  watch(sectionsToPreloadData, () => {
    if (hasSectionsToPreload.value && !preloadedData.value) {
      fetch();
    }
  });

  return {
    enhancedComponents,
  };
}
